import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  Users,
  Package,
  Percent,
  Shield,
  Star,
  ChevronRight,
  RefreshCw,
  Calendar,
  BarChart3,
  Wallet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// KPU Score Ring Component
const KPUScoreRing = ({ score, maxScore = 100, band, bandColor }) => {
  const percentage = (score / maxScore) * 100;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const getColorClass = () => {
    switch (bandColor) {
      case 'green': return 'text-green-500';
      case 'yellow': return 'text-yellow-500';
      case 'orange': return 'text-orange-500';
      case 'red': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-32 h-32 transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="10"
          fill="none"
          className="text-gray-200"
        />
        {/* Progress circle */}
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          className={getColorClass()}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: strokeDashoffset,
            transition: 'stroke-dashoffset 1s ease-in-out'
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-gray-800">{Math.round(score)}</span>
        <span className="text-xs text-gray-500">/ {maxScore}</span>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, subValue, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  );
};

// Funnel Stage Component
const FunnelStage = ({ label, count, value, percentage, isLast }) => (
  <div className="flex items-center">
    <div className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-semibold text-gray-800">{count}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-[#2b3380] h-2 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">₹{value?.toLocaleString()}</p>
    </div>
    {!isLast && <ChevronRight className="w-5 h-5 text-gray-300 mx-2" />}
  </div>
);

// KPU Breakdown Bar
const KPUBreakdownBar = ({ category, score, maxScore, label }) => {
  const percentage = (score / maxScore) * 100;
  
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold text-gray-800">{score}/{maxScore}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-500 ${
            percentage >= 80 ? 'bg-green-500' :
            percentage >= 60 ? 'bg-yellow-500' :
            percentage >= 40 ? 'bg-orange-500' :
            'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Main Dashboard Component
const EngineerPerformanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [engineer, setEngineer] = useState(null);

  useEffect(() => {
    // Get logged in engineer from localStorage
    const stored = localStorage.getItem('engineer');
    if (stored) {
      setEngineer(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    if (engineer?.employee_id) {
      fetchDashboard();
    }
  }, [engineer, period]);

  const fetchDashboard = async () => {
    if (!engineer?.employee_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API}/api/engineer-performance/dashboard/${engineer.employee_id}?period=${period}`
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to load performance data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  if (!engineer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Please login to view your performance</p>
          <button
            onClick={() => navigate('/engineer')}
            className="mt-4 px-6 py-2 bg-[#2b3380] text-white rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <p className="text-gray-500">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-6 py-2 bg-[#2b3380] text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { summary, kpu, funnel, metrics, incentive } = data || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#2b3380] to-[#4a5cc5] text-white px-4 pt-12 pb-8">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate('/engineer')} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <button 
            onClick={handleRefresh}
            className="p-2 bg-white/10 rounded-full"
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        <h1 className="text-xl font-bold mb-1">Performance Dashboard</h1>
        <p className="text-blue-200 text-sm">{engineer.name || engineer.employee_id}</p>
        
        {/* Period Selector */}
        <div className="flex gap-2 mt-4">
          {['day', 'week', 'month'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                period === p 
                  ? 'bg-white text-[#2b3380]' 
                  : 'bg-white/10 text-white'
              }`}
            >
              {p === 'day' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* KPU Score Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">KPU Score</h2>
              <p className="text-sm text-gray-500">Key Performance Unit</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
              kpu?.band_color === 'green' ? 'bg-green-100 text-green-700' :
              kpu?.band_color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
              kpu?.band_color === 'orange' ? 'bg-orange-100 text-orange-700' :
              'bg-red-100 text-red-700'
            }`}>
              {kpu?.band === 'Star' && '⭐ '}{kpu?.band}
            </div>
          </div>
          
          <KPUScoreRing 
            score={kpu?.total_score || 0} 
            maxScore={kpu?.max_score || 100}
            band={kpu?.band}
            bandColor={kpu?.band_color}
          />
          
          {kpu?.override_reason && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">{kpu.override_reason}</span>
              </div>
            </div>
          )}
          
          <p className="text-center text-sm text-gray-500 mt-4">{kpu?.band_action}</p>
        </div>

        {/* Summary Strip */}
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            icon={Package}
            label="Successful Pickups"
            value={summary?.successful_pickups || 0}
            subValue={`of ${summary?.leads_assigned || 0} leads`}
            color="blue"
          />
          <MetricCard
            icon={DollarSign}
            label="Value Secured"
            value={`₹${((summary?.net_value_secured || 0) / 1000).toFixed(1)}K`}
            color="green"
          />
          <MetricCard
            icon={Percent}
            label="Negotiation Success"
            value={`${summary?.negotiation_success_percent || 0}%`}
            color="purple"
          />
          <MetricCard
            icon={Shield}
            label="QC Accuracy"
            value={`${summary?.qc_accuracy_percent || 0}%`}
            color={summary?.qc_accuracy_percent >= 95 ? 'green' : 'orange'}
          />
        </div>

        {/* Value Drop Alert */}
        {summary?.value_drop_percent > 5 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-orange-700">
              <TrendingDown className="w-5 h-5" />
              <span className="font-semibold">Value Drop Alert</span>
            </div>
            <p className="text-sm text-orange-600 mt-1">
              {summary.value_drop_percent}% value drop from quoted to final price
            </p>
          </div>
        )}

        {/* Risk Flags */}
        {summary?.risk_flags > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">{summary.risk_flags} Risk Flag{summary.risk_flags > 1 ? 's' : ''}</span>
            </div>
            <p className="text-sm text-red-600 mt-1">
              Review and resolve risk events to improve your KPU score
            </p>
          </div>
        )}

        {/* KPU Breakdown */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">KPU Breakdown</h3>
          
          <KPUBreakdownBar 
            label="💰 Value & Money" 
            score={kpu?.breakdown?.value_money?.score || 0} 
            maxScore={35} 
          />
          <KPUBreakdownBar 
            label="🤝 Conversion" 
            score={kpu?.breakdown?.conversion?.score || 0} 
            maxScore={20} 
          />
          <KPUBreakdownBar 
            label="⚡ Productivity" 
            score={kpu?.breakdown?.productivity?.score || 0} 
            maxScore={15} 
          />
          <KPUBreakdownBar 
            label="✅ Quality" 
            score={kpu?.breakdown?.quality?.score || 0} 
            maxScore={20} 
          />
          <KPUBreakdownBar 
            label="😊 Customer" 
            score={kpu?.breakdown?.customer?.score || 0} 
            maxScore={10} 
          />
        </div>

        {/* Lead Funnel */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Lead → Money Funnel</h3>
          
          <div className="space-y-4">
            <FunnelStage 
              label="Leads Assigned" 
              count={funnel?.leads_assigned?.count || 0}
              value={funnel?.leads_assigned?.value || 0}
              percentage={100}
            />
            <FunnelStage 
              label="Negotiated" 
              count={funnel?.leads_negotiated?.count || 0}
              value={funnel?.leads_negotiated?.value || 0}
              percentage={funnel?.leads_assigned?.count ? 
                (funnel.leads_negotiated.count / funnel.leads_assigned.count) * 100 : 0}
            />
            <FunnelStage 
              label="Successful Pickups" 
              count={funnel?.successful_pickups?.count || 0}
              value={funnel?.successful_pickups?.value || 0}
              percentage={funnel?.leads_assigned?.count ? 
                (funnel.successful_pickups.count / funnel.leads_assigned.count) * 100 : 0}
              isLast
            />
          </div>
        </div>

        {/* Incentive Panel */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Incentive</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              incentive?.status === 'Eligible' ? 'bg-green-100 text-green-700' :
              'bg-red-100 text-red-700'
            }`}>
              {incentive?.status === 'Eligible' ? '✓ Eligible' : '✗ Blocked'}
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Gross Incentive</span>
              <span className="font-semibold text-gray-800">₹{(incentive?.gross || 0).toLocaleString()}</span>
            </div>
            
            {incentive?.penalties > 0 && (
              <div className="flex items-center justify-between text-red-600">
                <span>Penalties</span>
                <span>-₹{incentive.penalties.toLocaleString()}</span>
              </div>
            )}
            
            {incentive?.blocked > 0 && (
              <div className="flex items-center justify-between text-orange-600">
                <span>Blocked</span>
                <span>-₹{incentive.blocked.toLocaleString()}</span>
              </div>
            )}
            
            <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
              <span className="font-semibold text-gray-800">Net Payable</span>
              <span className="text-xl font-bold text-green-600">₹{(incentive?.net_payable || 0).toLocaleString()}</span>
            </div>
          </div>
          
          {incentive?.blocked_reasons?.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-1">Blocked Reasons:</p>
              <ul className="text-sm text-red-600">
                {incentive.blocked_reasons.map((reason, i) => (
                  <li key={i}>• {reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Quick Metrics */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Detailed Metrics</h3>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Avg Pickups/Day</p>
              <p className="font-semibold text-gray-800">{metrics?.productivity?.avg_pickups_per_day || 0}</p>
            </div>
            <div>
              <p className="text-gray-500">On-Time %</p>
              <p className="font-semibold text-gray-800">{metrics?.productivity?.on_time_percent || 0}%</p>
            </div>
            <div>
              <p className="text-gray-500">First Attempt %</p>
              <p className="font-semibold text-gray-800">{metrics?.productivity?.first_attempt_percent || 0}%</p>
            </div>
            <div>
              <p className="text-gray-500">Process Compliance</p>
              <p className="font-semibold text-gray-800">{metrics?.compliance?.process_compliance || 0}%</p>
            </div>
            <div>
              <p className="text-gray-500">Value Drop</p>
              <p className={`font-semibold ${metrics?.value?.value_drop_percent > 5 ? 'text-red-600' : 'text-gray-800'}`}>
                {metrics?.value?.value_drop_percent || 0}%
              </p>
            </div>
            <div>
              <p className="text-gray-500">QC Rejections</p>
              <p className={`font-semibold ${metrics?.quality?.qc_rejections > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                {metrics?.quality?.qc_rejections || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-around">
        <button 
          onClick={() => navigate('/engineer')}
          className="flex flex-col items-center text-gray-400"
        >
          <Package className="w-6 h-6" />
          <span className="text-xs mt-1">Pickups</span>
        </button>
        <button className="flex flex-col items-center text-[#2b3380]">
          <BarChart3 className="w-6 h-6" />
          <span className="text-xs mt-1">Performance</span>
        </button>
        <button 
          onClick={() => navigate('/engineer/profile')}
          className="flex flex-col items-center text-gray-400"
        >
          <Users className="w-6 h-6" />
          <span className="text-xs mt-1">Profile</span>
        </button>
      </div>
    </div>
  );
};

export default EngineerPerformanceDashboard;
